import React, { useEffect, useMemo, useState } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, Typography, IconButton, CircularProgress, Box, Tooltip, TablePagination } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CollapsibleRow from '../../components/GearList/CollapsibleRow';
import { db, auth } from '../../firebase/config';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc } from 'firebase/firestore';
import calculateUtility from '../../utility/calculateUtility';

const TemplateInventoryPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        const uid = auth?.currentUser?.uid;
        if (!uid) {
            setItems([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const q = query(
            collection(db, 'users', uid, 'customItems'),
            orderBy('updatedAt', 'desc')
        );
        const unsub = onSnapshot(q, (snap) => {
            const list = [];
            snap.forEach((d) => {
                const data = d.data();
                const withId = { id: d.id, ...data };
                // Ensure utility present for display
                const itemWithUtility = { ...withId, utility: calculateUtility(withId) };
                list.push(itemWithUtility);
            });
            setItems(list);
            setLoading(false);
        }, (e) => {
            console.error('Failed to load inventory', e);
            setError('Failed to load your saved items.');
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const handleDelete = async (id) => {
        const uid = auth?.currentUser?.uid;
        if (!uid) return;
        try {
            await deleteDoc(doc(db, 'users', uid, 'customItems', id));
        } catch (e) {
            console.error('Delete failed', e);
            setError('Failed to delete item.');
        }
    };

    const renderActions = useMemo(() => (item) => (
        <Tooltip title="Delete">
            <span>
                <IconButton color="error" onClick={() => handleDelete(item.id)}>
                    <DeleteIcon />
                </IconButton>
            </span>
        </Tooltip>
    ), []);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Template Inventory</Typography>
            {!auth?.currentUser && (
                <Typography variant="body2" color="textSecondary">Sign in to view and manage your saved custom items.</Typography>
            )}
            {auth?.currentUser && (
                loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography>Loading...</Typography>
                    </Box>
                ) : (
                    <>
                        {items.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">You have no saved custom items yet.</Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell>Name</TableCell>
                                            <TableCell>Utility</TableCell>
                                            <TableCell>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((it) => (
                                            <CollapsibleRow key={it.id} item={it} onSelectItem={() => {}} renderActions={renderActions} />
                                        ))}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    component="div"
                                    count={items.length}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </TableContainer>
                        )}
                    </>
                )
            )}
            {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>{error}</Typography>
            )}
        </Paper>
    );
};

export default TemplateInventoryPage;
